import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Stack,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdArrowBack, MdCheckCircle, MdError, MdList } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "services/api";
import { getThemeColor } from "theme/colors";

interface AuthorizationLog {
  id: string;
  customerExternalId: string;
  attemptType: string;
  success: boolean;
  errorMessage?: string;
  requestDetails?: string;
  responseStatusCode?: number;
  responseDetails?: string;
  attemptedAt: string;
}

interface AuthorizationLogsResponse {
  serverId: string;
  serverName: string;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  logs: AuthorizationLog[];
}

const AuthorizationLogs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { serverId } = useParams<{ serverId: string }>();

  const [data, setData] = useState<AuthorizationLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [page, setPage] = useState(1);
  const [customerFilter, setCustomerFilter] = useState("");
  const [attemptTypeFilter, setAttemptTypeFilter] = useState<string>("");
  const [successFilter, setSuccessFilter] = useState<string>("");
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const pageSize = 20;

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, attemptTypeFilter, successFilter]);

  const loadLogs = async () => {
    if (!serverId) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (customerFilter.trim()) {
        params.append("customerFilter", customerFilter);
      }
      if (attemptTypeFilter) {
        params.append("attemptType", attemptTypeFilter);
      }
      if (successFilter) {
        params.append("success", successFilter);
      }

      const response = await apiClient.get(
        `/Servers/${serverId}/authorization-logs?${params.toString()}`,
      );
      setData(response.data);
    } catch (err: any) {
      setError(err.message || t("app.servers.authorization.logs.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadLogs();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const toggleRowExpansion = (logId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRowId(expandedRowId === logId ? null : logId);
  };

  if (loading && !data) {
    return (
      <Stack gap={4}>
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.600", _dark: "white" }}
            >
              {t("app.common.loading")}
            </Text>
          </Card.Body>
        </Card.Root>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <Flex justify="space-between" align="center">
        <HStack gap={3}>
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            aria-label={t("app.common.back")}
          >
            <MdArrowBack />
          </Button>
          <VStack align="start" gap={0}>
            <Heading size="lg">
              {t("app.servers.authorization.logs.title")}
            </Heading>
            {data && <Text fontSize="sm">{data.serverName}</Text>}
          </VStack>
        </HStack>
        <AskStrikeUpButton
          context={t("app.askStrikeUp.context.authorizationLogs")}
        />
      </Flex>

      {/* Filters */}
      <Card.Root>
        <Card.Body>
          <Stack gap={3}>
            <Flex gap={3} wrap="wrap">
              <Box flex="1" minW="200px" themeAware>
                <Input
                  placeholder={t(
                    "app.servers.authorization.logs.searchPlaceholder",
                  )}
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  size="sm"
                />
              </Box>
              <Box minW="150px" themeAware>
                <Select
                  items={[
                    {
                      label: t("app.servers.authorization.logs.allAttempts"),
                      value: "",
                    },
                    {
                      label: t("app.servers.authorization.logs.automatic"),
                      value: "Automatic",
                    },
                  ]}
                  value={[attemptTypeFilter]}
                  onValueChange={(details) =>
                    setAttemptTypeFilter(details.value[0])
                  }
                  size="sm"
                />
              </Box>
              <Box minW="150px" themeAware>
                <Select
                  items={[
                    {
                      label: t("app.servers.authorization.logs.allResults"),
                      value: "",
                    },
                    {
                      label: t("app.servers.authorization.logs.success"),
                      value: "true",
                    },
                    {
                      label: t("app.servers.authorization.logs.failed"),
                      value: "false",
                    },
                  ]}
                  value={[successFilter]}
                  onValueChange={(details) =>
                    setSuccessFilter(details.value[0])
                  }
                  size="sm"
                />
              </Box>
              <Button
                onClick={handleSearch}
                colorPalette="blue"
                variant="solid"
                size="sm"
              >
                {t("app.servers.authorization.logs.search")}
              </Button>
            </Flex>
          </Stack>
        </Card.Body>
      </Card.Root>

      {error && (
        <Card.Root borderColor="red.500">
          <Card.Body>
            <Text color="red.700">
              {t("app.servers.authorization.logs.loadFailed")}: {error}
            </Text>
          </Card.Body>
        </Card.Root>
      )}

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
        overflow="hidden"
      >
        <Card.Body p={0}>
          {loading ? (
            <Flex p={8} justify="center">
              <Text>{t("app.common.loading")}</Text>
            </Flex>
          ) : !data || data.logs.length === 0 ? (
            <VStack gap={4} py={8} themeAware>
              <MdList size={64} color="gray" />
              <Text fontSize="lg">
                {t("app.servers.authorization.logs.noLogsFound")}
              </Text>
              <Text fontSize="sm" textAlign="center">
                {t("app.servers.authorization.logs.noLogsDescription")}
              </Text>
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table.Root minW="800px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.servers.authorization.logs.status")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.servers.authorization.logs.customerId")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.servers.authorization.logs.attemptType")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.servers.authorization.logs.responseCode")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.servers.authorization.logs.attemptedAt")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.servers.authorization.logs.details")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {data.logs.map((log) => (
                    <>
                      <Table.Row key={log.id}>
                        <Table.Cell>
                          <HStack>
                            {log.success ? (
                              <MdCheckCircle color="green" size={20} />
                            ) : (
                              <MdError color="red" size={20} />
                            )}
                            <Badge
                              colorPalette={log.success ? "green" : "red"}
                              variant="outline"
                            >
                              {log.success
                                ? t("app.servers.authorization.logs.success")
                                : t("app.servers.authorization.logs.failed")}
                            </Badge>
                          </HStack>
                        </Table.Cell>
                        <Table.Cell>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            fontFamily="mono"
                          >
                            {log.customerExternalId}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette="blue" variant="outline">
                            {log.attemptType}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">
                            {log.responseStatusCode ||
                              t("app.common.notAvailable")}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">
                            {formatDate(log.attemptedAt)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => toggleRowExpansion(log.id, e)}
                          >
                            {expandedRowId === log.id
                              ? t("app.servers.authorization.logs.hide")
                              : t("app.servers.authorization.logs.show")}
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                      {expandedRowId === log.id && (
                        <Table.Row key={`${log.id}-details`}>
                          <Table.Cell
                            colSpan={6}
                            style={{ overflow: "visible", maxWidth: "100%" }}
                          >
                            <Box p={4} borderRadius="md" w="full">
                              <VStack align="stretch" gap={3}>
                                {log.errorMessage && (
                                  <Box>
                                    <Text
                                      fontWeight="bold"
                                      fontSize="sm"
                                      color="red.600"
                                      mb={1}
                                    >
                                      {t(
                                        "app.servers.authorization.logs.errorMessage",
                                      )}
                                      :
                                    </Text>
                                    <Text
                                      fontSize="sm"
                                      color="red.700"
                                      fontFamily="mono"
                                      style={{ wordBreak: "break-word" }}
                                    >
                                      {log.errorMessage}
                                    </Text>
                                  </Box>
                                )}
                                {log.requestDetails && (
                                  <Box>
                                    <Text
                                      fontWeight="bold"
                                      fontSize="sm"
                                      mb={1}
                                    >
                                      {t(
                                        "app.servers.authorization.logs.requestDetails",
                                      )}
                                      :
                                    </Text>
                                    <Box
                                      p={2}
                                      borderRadius="md"
                                      fontSize="xs"
                                      fontFamily="mono"
                                    >
                                      <pre
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {JSON.stringify(
                                          JSON.parse(log.requestDetails),
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    </Box>
                                  </Box>
                                )}
                                {log.responseDetails && (
                                  <Box>
                                    <Text
                                      fontWeight="bold"
                                      fontSize="sm"
                                      mb={1}
                                    >
                                      {t(
                                        "app.servers.authorization.logs.responseDetails",
                                      )}
                                      :
                                    </Text>
                                    <Box
                                      p={2}
                                      borderRadius="md"
                                      fontSize="xs"
                                      fontFamily="mono"
                                      maxH="300px"
                                      overflowY="auto"
                                    >
                                      <pre
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {log.responseDetails}
                                      </pre>
                                    </Box>
                                  </Box>
                                )}
                              </VStack>
                            </Box>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Flex justify="center" align="center" gap={2}>
          <Button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            size="sm"
            variant="outline"
          >
            {t("app.servers.authorization.logs.previous")}
          </Button>
          <Text fontSize="sm">
            {t("app.servers.authorization.logs.page")} {page}{" "}
            {t("app.servers.authorization.logs.of")} {data.totalPages}
          </Text>
          <Button
            onClick={() => setPage(Math.min(data.totalPages, page + 1))}
            disabled={page >= data.totalPages}
            size="sm"
            variant="outline"
          >
            {t("app.servers.authorization.logs.next")}
          </Button>
        </Flex>
      )}
    </Stack>
  );
};

export default AuthorizationLogs;
